/**
 * The contract every tool page on this site implements.
 *
 * Processing logic never lives here. It lives in the matching package under
 * `tools/<id>`, which knows nothing about React and can be lifted out on its
 * own. This file only describes how a tool asks for input and hands back
 * results for rendering.
 */

export type Values = Record<string, unknown>;

export type FieldType =
  'textarea' | 'text' | 'number' | 'select' | 'checkbox' | 'radio' | 'file' | 'color' | 'range' | 'point' | 'grid';

export interface Field {
  name: string;
  label: string;
  type: FieldType;
  /** Starting value. Also what Reset returns to. */
  default?: unknown;
  options?: { value: string; label: string }[];
  placeholder?: string;
  /** One short sentence under the control. */
  help?: string;
  rows?: number;
  accept?: string;
  multiple?: boolean;
  min?: number;
  max?: number;
  step?: number;
  /** Render in a monospaced face. Defaults to true for textareas. */
  mono?: boolean;
  /** Hide this control unless the current values make it relevant. */
  visible?: (values: Values) => boolean;
  /** Lay the control out on its own full-width row. */
  wide?: boolean;
  /** Grid field only. Caps how many rows Add row can reach. Default 50. */
  maxRows?: number;
  /** Grid field only. Caps how many columns Add column can reach. Default 20. */
  maxColumns?: number;
  /** Point field only. Names the two axes shown beside the numeric inputs. Default ['X', 'Y']. */
  axes?: [string, string];
}

export type Tone = 'info' | 'warn' | 'error' | 'success';

/**
 * One element of the tree a `preview` output block draws inside its own
 * shadow root. `className` holds one or more space-separated class names;
 * the element carries no other attribute and no inline style -- the CSS
 * text the block ships is the only thing that ever styles it.
 */
export interface PreviewNode {
  className: string;
  text?: string;
  children?: PreviewNode[];
}

/**
 * One entry of a `tree` output block. `label` and `detail` are plain text: the block draws them as text and never as
 * markup. A node with `children` can be opened and closed; `open` asks for it to start open (without it, only the first
 * level starts open).
 */
export interface TreeNode {
  label: string;
  detail?: string;
  children?: TreeNode[];
  open?: boolean;
}

export interface OutputTable {
  headers: string[];
  rows: (string | number)[][];
  /** Column indexes to render in a monospaced face. */
  mono?: number[];
}

export interface DownloadableFile {
  name: string;
  mime: string;
  /** Text content, or bytes for binary results. */
  content: string | Uint8Array;
}

export type OutputBlock =
  | { kind: 'code'; label?: string; language?: string; value: string; download?: string }
  | { kind: 'text'; label?: string; value: string; download?: string }
  | { kind: 'keyvalue'; label?: string; pairs: [string, string][] }
  | { kind: 'table'; label?: string; table: OutputTable }
  | { kind: 'list'; label?: string; items: string[]; ordered?: boolean }
  | { kind: 'swatches'; label?: string; colors: { css: string; label: string; caption?: string }[] }
  /** HTML that the tool package has already sanitised. Rendered in a sandboxed frame. */
  | {
      kind: 'sandboxed-html';
      label?: string;
      html: string;
      /** False hides the Copy HTML button, for a preview that is not the tool's output. */
      copy?: boolean;
    }
  | { kind: 'image'; label?: string; src: string; alt: string; width?: number; height?: number; download?: string }
  | { kind: 'files'; label?: string; files: DownloadableFile[] }
  | { kind: 'note'; label?: string; tone: Tone; value: string }
  /**
   * A live preview stage shown beside its own exact CSS text. `tree` is the
   * element tree that `css` styles; `css` is applied inside the stage's own
   * shadow root as one constructed stylesheet, adopted there and nowhere
   * else, so the preview is styled by exactly what a visitor would paste
   * and by nothing from this site. `download` names the file a Download
   * button next to the CSS text saves it as.
   */
  | {
      kind: 'preview';
      label?: string;
      css: string;
      tree: PreviewNode;
      backdrop?: 'plain' | 'pattern';
      download?: string;
    }
  /**
   * The time left until `endsAt`, counted down on the page by the block itself (a 250 ms timer of its own, so nothing
   * else on the page re-renders). `endsAt` is a device-clock time in milliseconds since 1970 and `periodMs` the length of
   * the whole step, which sets the bar's full length. Shows whole seconds left, floored, and a native progress bar that is
   * hidden for visitors who ask for less motion. It never announces: the wrapper is a timer with live updates off.
   */
  | { kind: 'countdown'; label?: string; endsAt: number; periodMs: number }
  /**
   * Nested entries drawn as native expandable sections, in the order given, depth first. Text only: labels and details
   * are never read as markup. The block draws at most 5,000 nodes and 40 levels and says so when it cuts; Copy always
   * gives the whole tree, as `copyText` when the page supplies it and otherwise as indented text (two spaces per level,
   * a tab before a detail). `download` names a file that saves the same text. Keep the page's own tree under the size
   * the tool can hold in memory: the block caps what it draws, not what the page builds.
   */
  | { kind: 'tree'; label?: string; nodes: TreeNode[]; copyText?: string; download?: string }
  | { kind: 'diff'; label?: string; lines: { type: 'add' | 'del' | 'ctx' | 'meta'; text: string }[] };

export interface ToolIssue {
  message: string;
  line?: number;
  column?: number;
  path?: string;
}

export interface ToolResult {
  outputs: OutputBlock[];
  /** Problems with the input. Shown prominently; outputs may still render. */
  errors?: ToolIssue[];
  /** Things worth knowing that are not failures. */
  warnings?: string[];
  /** Small facts shown above the output, such as byte counts or timings. */
  stats?: [string, string][];
  /**
   * Where the `stats` block renders relative to `outputs`. Absent means
   * `'before-outputs'`, which is today's order (every stats entry before
   * every output block) -- so no page already live changes.
   *
   * This is a closed three-valued union rather than a two-state boolean on
   * purpose. The password-strength readout has a design-contract-fixed
   * order of verdict note, then stats, then a "Why" list, then a crack-time
   * table: the stats belong between the FIRST output block and the rest, a
   * position no boolean can express ("before all" or "after all" both give
   * the wrong order). `'after-first-output'` says exactly that.
   */
  statsPosition?: 'before-outputs' | 'after-first-output' | 'after-outputs';
  /**
   * Asks the runner to run the tool once more after this many milliseconds, with the values then in the form. The wait
   * is clamped to 250 to 60,000. There is one timer per result: a new result, an edit, Reset or leaving the page clears
   * it first, and a tool whose `autoRun` is false never gets one. A page that wants the timer again returns the field
   * again from the next run. A tab that was hidden or a page restored from the back-forward cache runs at once when it
   * becomes visible after the due time.
   */
  refreshAfterMs?: number;
}

export interface RunContext {
  /** Aborts when the user edits input again or leaves the page. */
  signal: AbortSignal;
  /**
   * Reports fractional progress (0-1) for long-running work, with an
   * optional detail string describing what stage it is at. Optional; most
   * tools never call it, and a tool that never calls it behaves exactly as
   * it does today -- no progress bar, no Cancel button, nothing rendered.
   */
  onProgress?(fraction: number, detail?: string): void;
}

/**
 * The time limit a page's own background helper enforces, so the working cue can say it. `ms` is always the helper's
 * exported limit constant, never a second number written on the page. `kind` is `'total'` (the default) when the helper
 * stops the run after `ms` in all, and `'quiet'` when it stops after `ms` with no progress.
 */
export interface RunLimit {
  ms: number;
  kind?: 'total' | 'quiet';
}

export interface ToolDocs {
  /** Two or three sentences on what the tool does and when to reach for it. */
  about: string;
  /** Exactly what is accepted. Be specific about versions and dialects. */
  supports: string[];
  /** What it will not do, and where it is lossy. Never leave this empty. */
  limits: string[];
  /** The specifications the behaviour is defined by, and tested against. */
  standards?: { label: string; url: string }[];
}

export interface ToolExample {
  label: string;
  values: Values;
}

export interface ToolPage {
  /** Must match a tool id in docs/catalog.json. */
  id: string;
  docs: ToolDocs;
  fields: Field[];
  examples?: ToolExample[];
  /**
   * Runs the tool. Must be pure with respect to the network: a local tool may
   * not perform fetch, XHR, WebSocket, sendBeacon or any other transmission.
   * The privacy test in `e2e/privacy.spec.ts` enforces this at runtime.
   */
  run(values: Values, ctx: RunContext): ToolResult | Promise<ToolResult>;
  /** Re-run as the user types. Off for tools with an expensive or file-based run. */
  autoRun?: boolean;
  /**
   * Declares that this tool's work can be abandoned part way through, and
   * that the visitor should be offered a control that does so. Absent
   * means false everywhere it is checked: no Cancel button ever renders,
   * and editing the form or pressing Reset never aborts an in-flight run.
   */
  cancellable?: boolean;
  /**
   * The limit the page's background helper enforces on a run, taken from the helper's own exported constant. A run still
   * going after about a second shows `Working… N s (stops at X s)` beside the output; a page with no limit shows the
   * elapsed time only.
   */
  runLimit?: RunLimit;
  /** Every tool in this release is 'local'. The value is displayed on the page. */
  processing?: 'local';
}

export function defineTool(page: ToolPage): ToolPage {
  return { autoRun: true, processing: 'local', ...page };
}

/** Reads a field, falling back to its declared default. */
export function val<T>(values: Values, field: string, fallback: T): T {
  const v = values[field];
  return v === undefined || v === null || v === '' ? fallback : (v as T);
}

export function bool(values: Values, field: string, fallback = false): boolean {
  const v = values[field];
  return typeof v === 'boolean' ? v : fallback;
}

export function num(values: Values, field: string, fallback: number): number {
  const v = values[field];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const parsed = Number(v);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

export function str(values: Values, field: string, fallback = ''): string {
  const v = values[field];
  return typeof v === 'string' ? v : fallback;
}

export function files(values: Values, field: string): File[] {
  const v = values[field];
  return Array.isArray(v) ? (v as File[]) : [];
}

/**
 * A short human-readable size string: bytes below the kilobyte threshold
 * (no decimal place), then kilobytes, megabytes or gigabytes on powers of
 * 1024, one decimal place each. Pure, so three tool pages this phase that
 * report file sizes in `stats` stay consistent with each other.
 */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 1024) return `${Math.max(0, Math.round(n))} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

/** Convenience for the common "one block of text out" case. */
export function textResult(value: string, language?: string, label?: string): ToolResult {
  return { outputs: [{ kind: 'code', value, language, label }] };
}

export function errorResult(message: string, issue?: Omit<ToolIssue, 'message'>): ToolResult {
  return { outputs: [], errors: [{ message, ...issue }] };
}

/**
 * Reads a grid field's value as a rectangular copy of string[][]. Anything
 * malformed -- not an array, an empty array, or a value whose rows are not
 * themselves arrays of strings -- reads as a single blank cell rather than
 * throwing, since a tool's run() should never crash on a value the grid
 * control itself would never produce.
 */
export function grid(values: Values, field: string): string[][] {
  const v = values[field];
  if (!Array.isArray(v) || v.length === 0) return [['']];
  const rows: string[][] = v.map((row) =>
    Array.isArray(row) ? row.map((cell) => (typeof cell === 'string' ? cell : '')) : [''],
  );
  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  return rows.map((row) => {
    const padded = row.slice(0, width);
    while (padded.length < width) padded.push('');
    return padded;
  });
}

/**
 * Reads a point field's value as `{ x, y }`, falling back to `fallback`
 * (whole or per-axis) when the stored value is missing or either axis is
 * not a finite number.
 */
export function point(values: Values, field: string, fallback: { x: number; y: number }): { x: number; y: number } {
  const v = values[field] as { x?: unknown; y?: unknown } | undefined;
  const x = typeof v?.x === 'number' && Number.isFinite(v.x) ? v.x : fallback.x;
  const y = typeof v?.y === 'number' && Number.isFinite(v.y) ? v.y : fallback.y;
  return { x, y };
}
